import {EventEmitter, Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {CartManager} from '../../data/manager/cart/cart.manager';
import {SessionStorageManager} from '../../data/manager/session-storage/session-storage.manager';
import {SasCartManager} from '../../data/manager-v3/sas-subscription/sas-cart/sas-cart.manager';
import {SasListManager} from '../../data/manager-v3/sas-subscription/sas-list/sas-list.manager';
import {AddressManager} from '../../data/manager/customer/address/address.manager';
import {CustomerManager} from '../../data/manager/customer/customer/customer.manager';
import {CallCenterManager} from '../../data/manager/call-center/call-center.manager';
import {LocationManager} from '../../data/manager/location/location.manager';
import {ConfigurationManager} from '../../data/manager/configuration/configuration.manager';
import { LocalStorageManager } from 'src/app/data/manager/local-storage/local-storage.manager';
import { DeliveryTypeEnum } from 'src/app/data/networking/models/location/enum/delivery-type.enum';
import { AlgoliaManager } from '../../data/manager/algolia/algolia.manager';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { DeliveryTime } from 'src/app/data/networking/services-backend-multicountry/models/properties/delivery-type-time.type';
import { CreditCardManager } from 'src/app/data/manager/customer/credit-card/credit-card.manager';
import { OrderManager } from 'src/app/data/manager/order/order/order.manager';
import { EstimatedTimeResponse } from 'src/app/data/networking/models/order/responses/order-res';
import { PickingDate } from 'src/app/data/networking/models/picking-date/picking-date';
import { CustomerAddress } from 'src/app/data/networking/models/marketplace-courier/requests/shipping-req';
import { CourierManager } from 'src/app/data/manager/marketplace-courier/courier.manager';
import { FirebaseFlags } from 'src/app/data/networking/models/firebase/response/flags';
import { FirebaseManager } from 'src/app/data/manager/firebase/firebase.manager';
import { SubstitutesRes } from 'src/app/data/networking/models/cart/responses/substitutes-res';
import { Product } from 'src/app/data/networking/models/product/responses/product.class';
import { UploadImgResponseInterface } from 'src/app/data/networking/models/legal/responses/legal-res';
import {DeliveryTypeTimeResponse} from 'src/app/data/networking/models/delivery-type-time/delivery-type-time.model';
import {CartRes} from 'src/app/data/networking/models/cart/responses/cart-res';
import {UpdateCartEvent} from 'src/app/ui/shared/event-listeners/update-cart/update-cart.event';

@Injectable({
  providedIn: 'root',

})
export class CartVm {
  
  pickingDate = new EventEmitter();
  
  constructor(
    private cartManager: CartManager,
    private ssm: SessionStorageManager,
    private lsm: LocalStorageManager,
    private sasCartManager: SasCartManager,
    private sasListManager: SasListManager,
    private addressManager: AddressManager,
    private customerManager: CustomerManager,
    private locationManager: LocationManager,
    private callCenterManager: CallCenterManager,
    private configurationManager: ConfigurationManager,
    private algoliaManager: AlgoliaManager,
    private creditCardManager: CreditCardManager,
    private courierManager: CourierManager,
    private orderManager: OrderManager,
    private firebaseManager: FirebaseManager,
    private updateCartEvent: UpdateCartEvent,
  ) {}

  getPrimePlans() {
    return this.lsm.get("primeConfig")[0].prime_plans;
  }

  get(address?:CustomerAddress): Observable<any> {
    return this.cartManager.get(null, address);
  }

  delete(params): Observable<any> {
    return this.cartManager.deleteCart(params);
  }

  getGeneralConfig(): any {
    const generalConfig = this.ssm.get('multiCountry');
    return (generalConfig && generalConfig.general) ? generalConfig.general : {};
  }

  getDeliveryTime():any{
    const delivery = this.ssm.get('multiCountry');
    return (delivery && delivery.delivery) ? delivery.delivery : {};
  }

  getDelivery():any{
    const delivery = this.ssm.get('deliveryTypeText');
    return delivery ? delivery : {};
  }

  getAdvertisedItems(): Observable<any> {
    return this.cartManager.getAdvertisedItems();
  }

  updateQuantity(data, filters?): Observable<any> {
    return this.cartManager.addProduct(data, (filters) ? filters : null );
  }

  removeProduct(product): Observable<any> {
    const deliveryType = !!this.lsm.get('mobileSaG') ? DeliveryTypeEnum.SCANANDGO : DeliveryTypeEnum.EXPRESS;
    return this.cartManager.removeProduct({...product, deliveryType});
  }

  getHighDemand(): Observable<any> {
    return this.cartManager.getHighDemand();
  }

  getHighDemandVzla(): Observable<any> {
    return this.cartManager.getHighDemandVzla();
  }

  getSubstitutes(products): Observable<any> {
    return this.cartManager.getSubstitutes(products);
  }

  handleSubstitutes(products): Observable<any> {
    return this.cartManager.handleSubstitutes(products);
  }

  saveLocalCart(cart) {
    this.ssm.set('cart', cart);
  }

  getLocalCart() {
    const cart = this.ssm.get('cart');
    const minItemsCart = 0;
    const emptyCart = [];
    if( !cart ) { return emptyCart; }
    const resp = !!cart && cart.carts?.length > minItemsCart;
    return resp ? cart : emptyCart;
  }

  savePaymentCardId(data) {
    this.ssm.set('paymentCardId', data);
  }

  getSasCart(): Observable<any> {
    return this.sasCartManager.getCart();
  }

  updateSasProductQuantity(data): Observable<any> {
    return this.sasCartManager.createDetail(data);
  }

  removeSasProduct(data): Observable<any> {
    return this.sasCartManager.removeSasProduct(data);
  }

  getSasBilling(): Observable<any> {
    return this.sasCartManager.getBilling();
  }

  getSasLocalCart() {
    return this.sasCartManager.getLocalCart();
  }

  deleteSasCart(): Observable<any> {
    return this.sasCartManager.delete();
  }

  saveSasLocalCart(cart, billing) {
    this.ssm.set('sasCart', {...cart, billing});
  }

  deleteAllCarts(cart, sasCart): Observable<any> {
    let observables = [];
    if (cart) {
      observables = [this.delete({hasCoupon: cart.hasCoupon})];
    }
    if (sasCart) {
      observables = [...observables, this.deleteSasCart()];
    }
    return forkJoin(...observables);
  }

  getPeriodicityText(data): Observable<any> {
    return this.sasListManager.getPeriodicityText(data);
  }

  getDeliveryAddress() {
    return this.addressManager.getDeliveryAddress();
  }

  getAddressUser(): Observable<any> {
    return this.addressManager.get();
  }

  hasCustomer() {
    return this.customerManager.hasValidCustomer();
  }

  isSasEnabled() {
    const config = this.getGeneralConfig();
    if (!config.programAndSave) {
      return false;
    }
    return this.sasCartManager.isSasEnabled();
  }

  callCenterCustomer() {
    return this.callCenterManager.getCustomer();
  }

  isLifeMilesEnabled() {
    return this.ssm.get('lifeMilesEnabled');
  }

  getAgile(): Observable<any> {
    return this.cartManager.getAgile(true);
  }

  getConfiguration(): any {
    return this.configurationManager.getConfigurationModule('general');
  }

  getCity(): Observable<any> {
    return this.locationManager.getCity();
  }

  getPickingDate(): PickingDate {
    return this.ssm.get('pickingDate');
  }

  savePickingDate(data): void {
    this.ssm.set('pickingDate', data);
  }

  removePickingDate() {
    this.ssm.removeItem('pickingDate');
  }

  getLabelTimeExpress() {
    return this.ssm.get('deliveryTypeText');
  }

  sendEventTalonOne(data):Observable<any>{
    return this.cartManager.sendEventTalonOne(data)
  }

  getLensesItemsCart(){
    return this.lsm.get('lenses');
  }

  getPropertiesAlgoliaHighDemand(objectID):Observable<any>{
    return this.algoliaManager.getPropertiesAlgolia(objectID);
  }

  getVarableDeliveryAddress(){
    return this.lsm.get('deliveryAddress');
  }

  objectSearch(objectID, index?){
    return this.algoliaManager.objectSearch(objectID, index)
  }

  getMessageOptimalRoute(){
    return this.algoliaManager.getConfigOptimalRouteConfig()
  }

  getDeliveryTypeText(): Observable<any> {
    return this.locationManager.getDeliveryTypeText().pipe(map(res => res.deliveryTimeLabelCart));
  }

  getCreditCards(): Observable<any> {
    return this.creditCardManager.get();
  }

  getScheduleConfig(): Observable<any> {
    const products = this.ssm.get('productsSubstitutes') || [];

    if (!products.length) {
      return this.cartManager.getScheduleConfig();
    }

    return this.cartManager.getSubstitutes(products)
      .pipe(
        switchMap((optimalRouteResponse: SubstitutesRes) => {
          const nearestStore = optimalRouteResponse?.nearestStore || null;

          return this.cartManager.getScheduleConfig(nearestStore);
        }),
        catchError(() => this.cartManager.getScheduleConfig())
      );
  }
  getDaneCode(cityId:string):Observable<string> {
    return this.courierManager.getAddress(cityId);
  }
  getEstimatedTime(): Observable<EstimatedTimeResponse> {
    const idAddress = this.lsm.get('deliveryAddress')?.idAddress;

    if (!idAddress) { return this.orderManager.getEstimatedTime(); }

    return this.orderManager.getEstimatedTime(idAddress);
  }

  forceToSchedule(): boolean {
    const forceToSchedule = this.ssm.get('forceToSchedule');
    const mandatory = this.ssm.get('mandatory');
    return Boolean(forceToSchedule || mandatory);
  }

  cleanForceToSchedule(): void {
    this.ssm.removeItem('forceToSchedule');
    this.ssm.removeItem('mandatory');
  }

  getLocalStorge(key: string) {
    return this.lsm.get(key);
  }

  getRemoteConfig(): Observable<FirebaseFlags> {
    return this.firebaseManager.getRemoteConfig();
  }

  uploadPrescription( prescriptionData ):Observable<UploadImgResponseInterface> {
    return this.orderManager.uploadPrescription(prescriptionData);
  }
  
  saveTemporalPrescriptionItems( prescriptionItems ):void {
    this.ssm.set('prescriptionItems', prescriptionItems);
  }

  validatePrescriptionItems():boolean {
    return !!this.ssm.get('prescriptionItems');
  }

  getTemporalPrescriptionItems():Product[] {
    return this.ssm.get('prescriptionItems').map((prescriptionItem:Product) => prescriptionItem);
  }

  savePrescriptionFileURL(prescriptionData):void{
    this.ssm.set('prescriptionFileURL', prescriptionData);
  }

  getPrescriptionFileURL():string{
    return this.ssm.get('prescriptionFileURL');
  }

  clearPrescriptionFileURL():void{
    this.ssm.removeItem('prescriptionFileURL');
  }

  getDeliveryTypeTime(): Observable<DeliveryTypeTimeResponse> {
    return this.orderManager.getDeliveryTypeTime();
  }

  updatedAgileCart(): Observable<CartRes> {
    return this.updateCartEvent.updatedAgileCart;
  }
}
